import React, { useCallback, useEffect, useRef } from 'react'
import classnames from 'classnames/bind'
import { AutomationTarget } from '../../../types'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface ScrollAreaProps extends AutomationTarget {
  /** Content to render inside the horizontally scrolling strip. */
  children: React.ReactNode
  /** Class applied to the outer scroll container. */
  className?: string
  /** Class applied to the inner content wrapper. */
  contentClassName?: string
  /**
   * Called whenever the content's overflow state changes. Consumers can use
   * this to render edge affordances (e.g. shadows) when the strip overflows.
   */
  onOverflowChange?: (hasOverflow: boolean) => void
  /**
   * When true, a vertical mouse-wheel gesture scrolls the strip horizontally.
   * Defaults to true (matches the legacy react-scrollbar `swapWheelAxes`).
   */
  swapWheelAxes?: boolean
}

/**
 * A lightweight horizontal scroll container built on native overflow scrolling.
 *
 * Replaces the abandoned `react-scrollbar` `ScrollArea` (legacy
 * `childContextTypes`, removed in React 19). Provides horizontal-only scroll,
 * a hover-revealed scrollbar (CSS), smooth scrolling, vertical-wheel-to-
 * horizontal remapping, and overflow detection via `ResizeObserver`.
 */
const ScrollArea: React.FC<ScrollAreaProps> = ({
  children,
  className,
  contentClassName,
  onOverflowChange,
  swapWheelAxes = true,
  elmId = 'scroll_area',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const hasOverflowRef = useRef(false)

  const checkOverflow = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const overflow = container.scrollWidth > container.clientWidth
    if (overflow !== hasOverflowRef.current) {
      hasOverflowRef.current = overflow
      onOverflowChange?.(overflow)
    }
  }, [onOverflowChange])

  useEffect(() => {
    checkOverflow()
    // ResizeObserver is not available in every environment (e.g. JSDOM).
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => checkOverflow())
    if (containerRef.current) observer.observe(containerRef.current)
    if (contentRef.current) observer.observe(contentRef.current)
    return () => observer.disconnect()
  }, [checkOverflow])

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (!swapWheelAxes) return
      const container = containerRef.current
      if (!container) return
      // Only remap when there is horizontal room and the gesture is vertical.
      if (
        container.scrollWidth > container.clientWidth &&
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
      ) {
        container.scrollLeft += event.deltaY
      }
    },
    [swapWheelAxes]
  )

  return (
    <div
      ref={containerRef}
      className={cx('scroll-area', className)}
      data-elm-id={elmId}
      onWheel={handleWheel}
    >
      <div
        ref={contentRef}
        className={cx('scroll-area-content', contentClassName)}
      >
        {children}
      </div>
    </div>
  )
}

export default ScrollArea
